import { useEffect, useState } from "react";
import { Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageTitle, Panel } from "@/components/studio/common";
import { useAuth } from "@/context/AuthContext";
import { api, API, dt, d, eur, formatErr } from "@/lib/api";

export default function ConsapImport() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [q, setQ] = useState("");
  const [records, setRecords] = useState([]);

  const load = () => api.get("/admin/consap/stats").then((r) => setStats(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/consap/records", { params: { q } }).then((r) => setRecords(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, stats]);

  const submit = async () => {
    if (!file) return toast.error("Seleziona un file CSV o Excel");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    try { const { data } = await api.post("/admin/consap/import", fd); setResult(data); toast.success(`${data.count} record importati`); load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="consap-import-page">
      <PageTitle title="Elenco Consap" sub={`${stats?.total ?? "…"} record attualmente in archivio`}>
        <Button asChild variant="outline" data-testid="consap-template-btn"><a href={`${API}/admin/consap/template`}><Download className="h-4 w-4 mr-2" />Modello CSV</a></Button>
      </PageTitle>
      {user.role === "admin" && (
        <Panel title="Importa nuovo elenco" className="mb-6" testid="consap-import-panel">
          <p className="text-sm text-slate-600 mb-4">Colonne riconosciute: cognome, nome (oppure nominativo), data_nascita, codice_fiscale, importo, banca/intermediario, rapporto. Separatore CSV rilevato automaticamente. Dopo l'import il matching viene rieseguito sui lead aperti.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <label className="flex-1 flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-lg px-4 h-11 cursor-pointer hover:border-navy">
              <Upload className="h-4 w-4 text-slate-500" /><span className="text-sm text-slate-600 truncate">{file ? file.name : "Scegli file .csv / .xlsx"}</span>
              <input data-testid="consap-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            </label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="consap-mode-select" className="sm:w-56 h-11"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="append">Aggiungi ai record esistenti</SelectItem><SelectItem value="replace">Sostituisci l'intero elenco</SelectItem></SelectContent>
            </Select>
            <Button data-testid="consap-import-btn" onClick={submit} disabled={busy} className="h-11 bg-navy hover:bg-navy-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Importa"}</Button>
          </div>
          {result && (
            <div data-testid="consap-import-result" className="mt-4 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              Importati <strong>{result.count}</strong> record · righe scartate {result.errors} · nuovi match sui lead: <strong>{result.new_matches}</strong>
              {result.error_samples?.length > 0 && <ul className="mt-2 text-xs text-amber-800">{result.error_samples.map((e) => <li key={e}>{e}</li>)}</ul>}
            </div>
          )}
        </Panel>
      )}
      <div className="grid xl:grid-cols-[1fr_360px] gap-6">
        <Panel title="Consulta record" testid="consap-records-panel">
          <Input data-testid="consap-search-input" placeholder="Cerca per cognome o codice fiscale" value={q} onChange={(e) => setQ(e.target.value)} className="mb-4" />
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500"><tr>{["Nominativo", "Nascita", "C.F.", "Intermediario", "Importo"].map((h) => <th key={h} className="text-left py-2 pr-3">{h}</th>)}</tr></thead>
            <tbody>{records.map((r) => (
              <tr key={r.id} className="border-t border-slate-100"><td className="py-2 pr-3 font-medium">{r.cognome} {r.nome}</td><td className="pr-3">{d(r.data_nascita)}</td>
                <td className="pr-3 font-mono text-xs">{r.codice_fiscale || "—"}</td><td className="pr-3">{r.banca}</td><td>{eur(r.importo)}</td></tr>))}</tbody>
          </table></div>
        </Panel>
        <Panel title="Storico import" testid="consap-imports-history">
          <ul className="space-y-3 text-sm">
            {stats?.imports.map((i) => (
              <li key={i.id} className="border-b border-slate-100 pb-2"><p className="font-medium truncate">{i.filename}</p>
                <p className="text-xs text-slate-500">{dt(i.at)} · {i.count} record · {i.mode === "replace" ? "sostituzione" : "aggiunta"} · {i.imported_by}</p></li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
